import React, { useContext, useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getStyle } from '@coreui/utils';
import { withFirebase } from '../firebase';
import RhythmCard from '../cards/RhythmCard';
import MiEScheduler from '../pages/scheduleManager/SchedPage';
import MiEMarket from '../pages/mieMarket/MiEMarket';
import MyStuff from '../pages/MyStuff/MyStuff';

import { ActiveLinkContext } from '../../components/ActiveLinkContext'; // Import the provider
import ManageAuctions from "../../components/ManageAuctions";
import { useDemoDataSeeding } from '../../utils/useDemoDataSeeding';
import FeatureTour from '../../components/FeatureTour';
import AdminPage from '../pages/adminPage';
import Creatures from '../pages/apikeys/KeysLayout';
import Steps from '../cards/Steps';

// Import other components
import CaloriesForm from '../cards/Calories';
import BloodSugarForm from '../cards/BloodSugar';
import CryptoDashboard from '../cards/CryptoDashboard';
import BloodPressureForm from '../cards/BloodPressure';
import DnaMetricsDashboard from '../cards/DnaMetricsDashboard';
import SuggestionsForm from '../cards/Suggestions';
import CCDViewer from '../cards/CCDViewer/CCDViewer';
import WeightForm from '../cards/Weight';
import SleepForm from '../cards/Sleep';
import MoodRingForm from '../cards/MoodRing';
import SocialDashboard from '../cards/SocialDashboard';
import SnapshotForm from '../cards/Snapshot';
import FriendsForm from '../cards/Friends';
import ExpensePieChart from '../cards/ExpensePieChart';
import MiENotes from '../pages/notes/Notes';
import HealthDashboard from '../cards/HealthDashboard/HealthDashboard';
import HomeAssistant from '../pages/homeassistant/HomeAssistant';
import BlockchainExplorer from '../../components/BlockchainExplorer';
import CreatorPage from '../pages/creatorPage/CreatorPage';
import CartPage from '../pages/cart/CartPage';
import OrdersPage from '../pages/orders/OrdersPage';
const brandPrimary = getStyle('--primary');
const brandSuccess = getStyle('--success');
const brandInfo = getStyle('--info');
const brandWarning = getStyle('--warning');
const brandDanger = getStyle('--danger');

const Dashboard = ({ authUser, fhirToken }) => {
  // const authUser = useContext(AuthUserContext);
  const [activeLinkOld, setActiveLinkOld] = useState('home'); // Default to 'home'
  const { activeLink, setActiveLink } = useContext(ActiveLinkContext); // Use the context
  const location = useLocation(); // Use React Router's useLocation hook
  
  // Hook to seed demo observation data for new users (runs automatically on first visit)
  // This ensures demo users have health data to visualize even if login flow didn't trigger it
  const { isSeeding, seedingComplete } = useDemoDataSeeding(authUser);

  // console.log('Dashboard rendered', { activeLink });
  const renderComponent = () => {
    //console.log(`Props for dash: authUser: ${JSON.stringify(authUser)}, FHIRTOKEN: ${fhirToken}`);
    switch (activeLink) {
      case 'home':
        return <RhythmCard authUser={authUser} fhirToken={fhirToken} />;
      case 'steps':
        return <Steps authUser={authUser} fhirToken={fhirToken} />;
      case 'health':
        return <HealthDashboard authUser={authUser} fhirToken={fhirToken} />;
      case 'calories':
        return <CaloriesForm authUser={authUser} fhirToken={fhirToken} />;
      case 'weight':
        return <WeightForm authUser={authUser} fhirToken={fhirToken} />;
      case 'bloodSugar':
        return <BloodSugarForm authUser={authUser} fhirToken={fhirToken} />;
      case 'bloodPressure':
        return <BloodPressureForm authUser={authUser} fhirToken={fhirToken} />;
      case 'crypto':
        return <CryptoDashboard authUser={authUser} fhirToken={fhirToken} />;
      case 'explorer':
        return <BlockchainExplorer authUser={authUser} fhirToken={fhirToken} />;
      case 'suggestions':
        return <SuggestionsForm authUser={authUser} fhirToken={fhirToken} />;
      case 'sleep':
        return <SleepForm authUser={authUser} fhirToken={fhirToken} />;
      case 'ccdconverter':
        return <CCDViewer authUser={authUser} fhirToken={fhirToken} />;
      case 'moodRing':
        return <MoodRingForm authUser={authUser} fhirToken={fhirToken} />;
      case 'expenses':
        return <ExpensePieChart authUser={authUser} fhirToken={fhirToken} />;
      case 'social':
        return <SocialDashboard authUser={authUser} fhirToken={fhirToken} />;
      case 'snapshot':
        return <SnapshotForm authUser={authUser} fhirToken={fhirToken} />;
      case 'dna':
        return <DnaMetricsDashboard authUser={authUser} fhirToken={fhirToken} />;
      case 'friends':
        return <FriendsForm authUser={authUser} fhirToken={fhirToken} />;
      case 'digiart':
        return <MyStuff authUser={authUser} fhirToken={fhirToken} />;
      case 'scheduler':
        return <MiEScheduler authUser={authUser} fhirToken={fhirToken} />;
        case 'notes':
          return <MiENotes authUser={authUser} fhirToken={fhirToken} />;
      case 'creatures':
        return <Creatures authUser={authUser} fhirToken={fhirToken} />;
      case 'profile':
        return <AdminPage authUser={authUser} fhirToken={fhirToken} />;
      case 'auctions':
        return <ManageAuctions authUser={authUser} fhirToken={fhirToken} />;
      case 'meta':
        return <UserGameFrame uid={authUser.uid} />;
      case 'homeassistant':
        return <HomeAssistant authUser={authUser} fhirToken={fhirToken} />;
      case 'hadevices':
        return <HomeAssistant authUser={authUser} fhirToken={fhirToken} initialTab="devices" />;
      case 'haautomations':
        return <HomeAssistant authUser={authUser} fhirToken={fhirToken} initialTab="automations" />;
      case 'haintegrations':
        return <HomeAssistant authUser={authUser} fhirToken={fhirToken} initialTab="integrations" />;
      case 'creator':
        return <CreatorPage authUser={authUser} fhirToken={fhirToken} />;
      case 'cart':
        return <CartPage authUser={authUser} fhirToken={fhirToken} />;
      case 'orders':
        return <OrdersPage authUser={authUser} fhirToken={fhirToken} />;
      default:
        return <RhythmCard authUser={authUser} fhirToken={fhirToken} />;
    }
  };

  const UserGameFrame = ({ uid }) => {
    const iframeRef = useRef(null);

    useEffect(() => {
      const handleGameReady = (event) => {
        if (event.data === 'GAME_READY') {
          iframeRef.current.contentWindow.postMessage({
            type: 'SET_USER_ID',
            uid: uid
          }, 'https://meta.mieros.cloud');
        }
      };

      window.addEventListener('message', handleGameReady);
      return () => window.removeEventListener('message', handleGameReady);
    }, [uid]);

    return (
      <iframe
        ref={iframeRef}
        src="https://meta.mieros.cloud/"
        title="Meta Mieros"
        style={{ width: '100%', height: '100vh', border: 'none' }}
      />
    );
  };

  useEffect(() => {
    // Get the navigation state
    const state = location.state;
    
    // If we have a state and activeLink doesn't match, update it
    if (state?.activeLink && state.activeLink !== activeLink) {
      setActiveLink(state.activeLink);
    }
  }, [location.state, activeLink, setActiveLink]);

  return (
    <div className="animated fadeIn">
      {authUser ? (
        <>
          {/* AgentSetupCard suppressed after login — not rendered on dashboard */}

          {/* Guided feature tour (renders only if not dismissed and react-joyride is installed) */}
          <FeatureTour />

          {renderComponent()}
          {/* Uncomment and use the forms as needed */}
          {/* <Row>
            <Col xs="12" sm="6" lg="3">
              <CaloriesForm authUser={authUser} />
            </Col>
            <Col xs="12" sm="6" lg="3">
              <BloodSugarForm authUser={authUser} />
            </Col>
            <Col xs="12" sm="6" lg="3">
              <BloodPressureForm authUser={authUser} />
            </Col>
            <Col xs="12" sm="6" lg="3">
              <SuggestionsForm authUser={authUser} />
            </Col>
          </Row> */}
          {/* <SleepForm authUser={authUser} />
          <MoodRingForm authUser={authUser} />
          <SocialForm authUser={authUser} />
          <SnapshotForm authUser={authUser} />
          <FriendsForm authUser={authUser} /> */}
        </>
      ) : (
        <p>Loading...</p>
      )}
    </div>
  );
};

export default withFirebase(Dashboard);
